import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CameraOval } from '../../components/onboarding/CameraOval.js';
import { OnboardingProgressBar } from '../../components/onboarding/OnboardingProgressBar.js';
import { apiUpload } from '../../lib/api-client.js';
import { useAuth } from '../../store/auth.js';
import { useMe } from '../../hooks/useMe.js';
import { hasCurrentBiometricConsent } from '../../components/avatarFlow/SelfieConsentModal.js';
import styles from './CameraCapture.module.css';

export function CameraCapture(): JSX.Element {
  const { t } = useTranslation('flows');
  const { t: tc } = useTranslation('common');
  const jwt = useAuth(s => s.jwt);
  const setStep = useAuth(s => s.setOnboardingStep);
  const nav = useNavigate();
  const location = useLocation();
  const { data: me } = useMe();
  const [preview, setPreview] = useState<{ blob: Blob; url: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const galleryTriggeredRef = useRef(false);

  useEffect(() => {
    const state = location.state as { openGallery?: boolean } | null;
    if (state?.openGallery && !galleryTriggeredRef.current) {
      galleryTriggeredRef.current = true;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = (ev) => {
        const f = (ev.target as HTMLInputElement).files?.[0];
        if (f) onCapture(f);
      };
      input.click();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  function onCapture(blob: Blob): void {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview({ blob, url: URL.createObjectURL(blob) });
    setError(null);
  }

  async function useThis(): Promise<void> {
    if (!jwt || !preview || submitting) return;
    // Belt-and-suspenders: refuse upload without current biometric consent (B.4)
    if (!hasCurrentBiometricConsent(me)) {
      setError(t('onboarding.capture.consentRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    const fd = new FormData();
    fd.append('file', preview.blob, 'selfie.jpg');
    try {
      const r = await apiUpload('/api/v1/star-persona/selfie', jwt, fd);
      if (r.status === 409) {
        setStep(7);
        nav('/onboarding/personal', { replace: true });
        return;
      }
      if (!r.ok) {
        if (r.status === 403) {
          const body = (await r.json().catch(() => null)) as { error?: string } | null;
          if (body?.error === 'age_gate') {
            setError(t('onboarding.capture.ageGate'));
            setSubmitting(false);
            return;
          }
        }
        setError(`upload_failed_${r.status}`);
        setSubmitting(false);
        return;
      }
      const j = await r.json() as { onboardingStep?: number };
      if (typeof j.onboardingStep === 'number') setStep(j.onboardingStep);
      nav('/onboarding/adding', { replace: true });
    } catch {
      setError('network_error');
      setSubmitting(false);
    }
  }

  function retake(): void {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setError(null);
  }

  return (
    <div className={styles.screen}>
      <OnboardingProgressBar step={5} etaSeconds={15} />
      {/* Header */}
      <h1 className={styles.title}>{t('onboarding.capture.title')}</h1>

      {/* Face-detected pill (static placeholder — FaceDetector API is experimental
          and not widely available in Telegram WebView yet) */}
      {!preview && (
        <span className={styles.facePill} aria-live="polite">
          <span className={styles.facePillDot} aria-hidden="true" />
          {t('onboarding.capture.facePill')}
        </span>
      )}

      {/* Viewfinder or preview */}
      <div className={styles.viewfinderArea}>
        {!preview ? (
          <CameraOval onCapture={onCapture} />
        ) : (
          <div className={styles.previewWrapper}>
            <img src={preview.url} alt="" className={styles.previewImg} />
          </div>
        )}
      </div>

      {/* Error */}
      {error && <p className={styles.errorText}>{error}</p>}

      {/* Actions */}
      {preview && (
        <div className={styles.previewActions}>
          <button
            type="button"
            className={styles.retakeBtn}
            onClick={retake}
            disabled={submitting}
          >
            {t('onboarding.capture.retake')}
          </button>
          <button
            type="button"
            className={styles.useBtn}
            onClick={() => void useThis()}
            disabled={submitting}
          >
            {submitting ? tc('states.saving') : t('onboarding.capture.cta.useThis')}
          </button>
        </div>
      )}
    </div>
  );
}
